import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Pencil, CalendarPlus, Trash2, PhoneCall, MessageSquare, User, Plus, Check, CalendarClock } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/Brand";
import ContactFiles from "@/components/ContactFiles";
import { api, errMsg, useData } from "@/lib/api";
import { fmtDateTime, fmtTime, fullName, STATUS_LABELS, TEMP_STYLE } from "@/lib/crm";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "—");

function CommLog({ contactId }) {
  const [items] = useData(`/contacts/${contactId}/activity`, []);
  if (!items.length) return <p className="text-xs text-stone-500">No interactions yet.</p>;
  return (
    <ol className="relative border-l border-[#D4AF37]/50 ml-2 space-y-4" data-testid="contact-comm-log">
      {items.map((a) => (
        <li key={a.id} className="ml-4">
          <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-[#3B185F]" />
          <div className="text-sm font-medium">{a.action}{a.detail && <span className="text-stone-500"> · {a.detail}</span>}</div>
          <div className="text-xs text-stone-500">{a.user_name} · {fmtDateTime(a.created_at)}</div>
        </li>
      ))}
    </ol>
  );
}

function TasksTab({ contactId }) {
  const [tasks, reload] = useData("/reminders", []);
  const mine = tasks.filter((t) => t.contact_id === contactId);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const add = async () => {
    if (!title.trim()) return;
    try {
      await api.post("/reminders", { type: "Follow-up", title: title.trim(), contact_id: contactId, due_date: due, priority: "Medium" });
      setTitle(""); setDue(""); reload(); toast.success("Task added");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const complete = async (t) => { try { await api.patch(`/reminders/${t.id}`, { done: !t.done }); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-3" data-testid="drawer-tasks">
      <div className="flex gap-2">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a follow-up task…" className="h-9" data-testid="drawer-task-title-input" />
        <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-9 w-36" data-testid="drawer-task-date-input" />
        <Button size="sm" className="bg-[#3B185F]" onClick={add} data-testid="drawer-task-add-btn"><Plus className="h-4 w-4" /></Button>
      </div>
      {!mine.length && <p className="text-xs text-stone-500">No tasks for this lead yet.</p>}
      {mine.map((t) => (
        <div key={t.id} className="flex items-start gap-2 rounded-lg border p-2.5" data-testid={`drawer-task-${t.id}`}>
          <button type="button" onClick={() => complete(t)} className={`mt-0.5 grid h-4 w-4 place-items-center rounded border ${t.done ? "bg-[#059669] border-[#059669] text-white" : "border-stone-300"}`} data-testid={`drawer-task-complete-${t.id}`}>{t.done && <Check className="h-3 w-3" />}</button>
          <div className="min-w-0 flex-1">
            <div className={`text-sm font-medium ${t.done ? "line-through text-stone-400" : "text-[#19082B]"}`}>{t.title}</div>
            <div className="text-xs text-stone-500">{t.type}{t.due_date ? ` · due ${t.due_date}` : ""}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CommsTab({ contact, navigate }) {
  const [sms] = useData(`/sms?contact_id=${contact.id}`, []);
  const [calls] = useData(`/calls?contact_id=${contact.id}`, []);
  return (
    <div className="space-y-5" data-testid="drawer-comms">
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="border-[#3B185F]/20" onClick={() => navigate("/dialer", { state: { contactId: contact.id } })} data-testid="drawer-comms-dial"><PhoneCall className="h-4 w-4 mr-1.5" />Dial</Button>
        <Button size="sm" variant="outline" className="border-[#3B185F]/20" onClick={() => navigate("/sms", { state: { contactId: contact.id } })} data-testid="drawer-comms-sms"><MessageSquare className="h-4 w-4 mr-1.5" />SMS</Button>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#3B185F] mb-2">Calls</div>
        {!calls.length && <p className="text-xs text-stone-500">No calls logged.</p>}
        <div className="space-y-2">
          {calls.map((c) => (
            <div key={c.id} className="rounded-lg border p-2.5" data-testid={`drawer-call-${c.id}`}>
              <div className="flex justify-between text-sm"><span className="font-medium text-[#19082B]">{c.disposition}</span><span className="text-xs text-stone-400">{fmtDateTime(c.created_at)}</span></div>
              {c.note && <div className="text-xs text-stone-500">{c.note}</div>}
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-[#3B185F] mb-2">Text messages</div>
        {!sms.length && <p className="text-xs text-stone-500">No messages sent.</p>}
        <div className="space-y-2">
          {sms.map((m) => (
            <div key={m.id} className="flex justify-end" data-testid={`drawer-sms-${m.id}`}>
              <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-[#3B185F] px-3 py-1.5 text-sm text-white">{m.body}<div className="text-[10px] text-white/60 text-right mt-0.5">{fmtTime(m.created_at)}</div></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ContactDrawer({ contact, onClose, onEdit, onDelete }) {
  const navigate = useNavigate();
  return (
    <Sheet open={!!contact} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="contact-drawer">
        {contact && (
          <div key={contact.id}>
            <SheetHeader className="text-left space-y-3">
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#C59B27] font-bold">Lead</span>
              <SheetTitle className="font-display text-3xl text-[#19082B]" data-testid="drawer-contact-name">{fullName(contact)}</SheetTitle>
              <div className="flex flex-wrap gap-2 items-center">
                {contact.temperature && <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TEMP_STYLE[contact.temperature]}`} data-testid="drawer-contact-temp">{contact.temperature}</span>}
                <StatusBadge status={contact.status} label={STATUS_LABELS[contact.status]} testId="drawer-contact-status" />
                {contact.assigned_to_name && <span className="inline-flex items-center gap-1 rounded-full bg-[#F6F2FB] px-2.5 py-0.5 text-xs text-[#3B185F]"><User className="h-3 w-3" />{contact.assigned_to_name}</span>}
                {contact.tags.map((t) => <span key={t} className="rounded-full bg-[#F6F2FB] px-2.5 py-0.5 text-xs text-[#3B185F]">{t}</span>)}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-stone-500" data-testid="drawer-contact-date"><CalendarClock className="h-3.5 w-3.5" />Added {fmtDate(contact.created_at)}</div>
            </SheetHeader>
            <div className="flex flex-wrap gap-2 my-5">
              <Button size="sm" onClick={() => onEdit(contact)} className="bg-[#3B185F]" data-testid="drawer-edit-btn"><Pencil className="h-4 w-4 mr-1.5" />Edit</Button>
              <Button size="sm" variant="outline" className="border-[#D4AF37]" onClick={() => navigate("/calendar", { state: { contactId: contact.id } })} data-testid="drawer-schedule-btn"><CalendarPlus className="h-4 w-4 mr-1.5" />Schedule</Button>
              <Button size="sm" variant="outline" className="ml-auto text-red-600 border-red-200 hover:bg-red-50" onClick={() => { if (window.confirm(`Delete ${fullName(contact)}? This removes the lead from Contacts and Pipeline.`)) onDelete(contact); }} data-testid="drawer-delete-btn"><Trash2 className="h-4 w-4 mr-1.5" />Delete</Button>
            </div>
            <Tabs defaultValue="details">
              <TabsList className="grid grid-cols-5 w-full">
                <TabsTrigger value="details" data-testid="drawer-tab-details">Details</TabsTrigger>
                <TabsTrigger value="tasks" data-testid="drawer-tab-tasks">Tasks</TabsTrigger>
                <TabsTrigger value="comms" data-testid="drawer-tab-comms">Dial/SMS</TabsTrigger>
                <TabsTrigger value="log" data-testid="drawer-tab-log">History</TabsTrigger>
                <TabsTrigger value="files" data-testid="drawer-tab-files">Files</TabsTrigger>
              </TabsList>
              <TabsContent value="details" className="space-y-3 pt-3 text-sm">
                <div className="flex gap-2"><Mail className="h-4 w-4 text-[#3B185F]" />{contact.email || "—"}</div>
                <div className="flex gap-2"><Phone className="h-4 w-4 text-[#3B185F]" />{contact.phone || "—"}</div>
                <div className="flex gap-2"><MapPin className="h-4 w-4 text-[#3B185F]" />{contact.address || "—"}</div>
                <div className="rounded-lg bg-[#FFFDF0] border border-[#D4AF37]/30 p-3">
                  <div className="text-xs font-semibold text-[#7a5c0c] mb-1">Notes</div>
                  <p className="whitespace-pre-wrap" data-testid="drawer-contact-notes">{contact.notes || "No notes yet."}</p>
                </div>
              </TabsContent>
              <TabsContent value="tasks" className="pt-3"><TasksTab contactId={contact.id} /></TabsContent>
              <TabsContent value="comms" className="pt-3"><CommsTab contact={contact} navigate={navigate} /></TabsContent>
              <TabsContent value="log" className="pt-3"><CommLog contactId={contact.id} /></TabsContent>
              <TabsContent value="files" className="pt-3"><ContactFiles contactId={contact.id} /></TabsContent>
            </Tabs>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
